import { useState, useLayoutEffect, useMemo } from 'react';

function getWindowDimensions() {
  const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
  const { outerWidth, outerHeight, innerWidth } = window;
  return {
    width: innerWidth,
    height: viewportHeight,
    viewportWidth,
    outerHeight,
    outerWidth,
  };
}

const isTextInputElement = (element: Element | null): boolean => {
  if (!element || !(element instanceof HTMLElement)) return false;
  const tag = element.tagName;
  if (tag === 'TEXTAREA') return true;
  if (tag !== 'INPUT') return element.isContentEditable;
  const input = element as HTMLInputElement;
  const nonTextTypes = new Set([
    'button',
    'checkbox',
    'color',
    'file',
    'hidden',
    'image',
    'radio',
    'range',
    'reset',
    'submit',
  ]);
  return !nonTextTypes.has((input.type || 'text').toLowerCase());
};

export const sidebarWidth = 320;

export const mainScreenMobilePadding = 16;

export const mainScreenPaddingVertical = 24;
export const mainScreenPaddingHorizontal = 36;

export const mainScreenPaddingVerticalDialog = 16;
export const mainScreenPaddingHorizontalDialog = 16;
export const mainScreenPaddingVerticalTablet = 24;
export const mainScreenPaddingHorizontalTablet = 36;
export const mainScreenPaddingTablet = `${mainScreenPaddingVerticalTablet}px ${mainScreenPaddingHorizontalTablet}px`;

export const videoCallControlContainerHeight = 160;
export const videoCallControlContainerHeightMobile = 80;
export const videoCallControlContainerHeightMobileExpanded = 160;

export const medicalSideBarWidth = 96;
export const medicalHeaderHeight = 72;

export function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions(),
  );

  useLayoutEffect(() => {
    function handleResize() {
      const nextDimensions = getWindowDimensions();
      const visualHeight = window.visualViewport?.height ?? window.innerHeight;
      const innerHeight = window.innerHeight;
      setWindowDimensions((prevDimensions) => {
        const isMobile = nextDimensions.width < 800;
        const activeElement =
          typeof document !== 'undefined' ? document.activeElement : null;
        const isTextInputFocused = isTextInputElement(activeElement);

        if (isMobile && isTextInputFocused) {
          // Ignore keyboard-open shrink; keep a stable app height while typing.
          return {
            ...nextDimensions,
            height: Math.max(prevDimensions.height, visualHeight, innerHeight),
          };
        }

        if (isMobile) {
          // After keyboard closes, some in-app browsers report stale visualViewport
          // for a short period. Prefer the larger value to restore header/layout.
          return {
            ...nextDimensions,
            height: Math.max(visualHeight, innerHeight),
          };
        }

        return { ...nextDimensions, height: visualHeight };
      });
    }

    window.addEventListener('resize', handleResize);
    window.visualViewport?.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('resize', handleResize);
    };
  }, []);

  const isDesktop = useMemo(() => {
    return windowDimensions.height >= 1080;
  }, [windowDimensions.height]);

  const isTablet = useMemo(() => {
    return windowDimensions.height >= 600 && windowDimensions.height < 1080;
  }, [windowDimensions.height]);

  const isMobile = useMemo(() => {
    return windowDimensions.width < 800;
  }, [windowDimensions.width]);

  const { LogoSize, LogoTextSize } = useMemo(() => {
    if (isDesktop) {
      return {
        LogoSize: 90,
        LogoTextSize: 40,
      };
    }
    if (isTablet) {
      return {
        LogoSize: 50,
        LogoTextSize: 28,
      };
    }
    if (isMobile) {
      return {
        LogoSize: 20,
        LogoTextSize: 16,
      };
    }
    return {
      LogoSize: 90,
      LogoTextSize: 40,
    };
  }, [isMobile, isTablet, isDesktop]);

  const mainScreenPadding = useMemo(() => {
    if (isMobile) {
      return `${mainScreenMobilePadding}px`;
    }
    return `${mainScreenPaddingVertical}px ${mainScreenPaddingHorizontal}px`;
  }, [isMobile]);

  const headerHeight = useMemo(() => {
    if (isDesktop) {
      return 120;
    }
    if (isMobile) {
      return 50;
    }
    if (isTablet) {
      return 80;
    }
    return 120;
  }, [isMobile, isTablet, isDesktop]);

  const footerHeight = useMemo(() => {
    if (isMobile || isTablet) {
      return 0;
    }
    return 50;
  }, [isMobile, isTablet]);

  const useWindowRatio = (size: number) => {
    return size;
  };
  const mainScreenSize = useMemo(() => {
    if (isMobile) {
      return {
        width: windowDimensions.width,
        height: windowDimensions.height - headerHeight - headerHeight,
      };
    }
    return {
      width: windowDimensions.width - sidebarWidth,
      height: windowDimensions.height - headerHeight,
    };
  }, [windowDimensions, headerHeight, sidebarWidth, isMobile]);

  const mainScreenSizeMedical = useMemo(() => {
    return {
      width: windowDimensions.width,
      height: windowDimensions.height - medicalHeaderHeight,
    };
  }, [windowDimensions, headerHeight, isMobile]);

  const infoDialogSize = useMemo(() => {
    if (isMobile) {
      return {
        width: windowDimensions.width * 0.8,
      };
    }
    return {
      width: Math.min(windowDimensions.width * 0.8, 400),
    };
  }, [windowDimensions, isMobile]);

  const mainScreenUsableSize = useMemo(() => {
    return {
      width: mainScreenSize.width - mainScreenPaddingHorizontal * 2,
      height: mainScreenSize.height - mainScreenPaddingVertical * 2,
    };
  }, [mainScreenSize]);

  const mainScreenUsableSizeMedical = useMemo(() => {
    return {
      width: mainScreenSizeMedical.width - mainScreenPaddingHorizontal * 2,
      height: mainScreenSizeMedical.height - mainScreenPaddingVertical * 2,
    };
  }, [mainScreenSizeMedical]);

  const mainScreenUsableSizeDialog = useMemo(() => {
    return {
      width: mainScreenSize.width - mainScreenPaddingHorizontal * 2,
      height: mainScreenSize.height - mainScreenPaddingVertical * 2,
    };
  }, [mainScreenSize]);

  const videoCallScreenSize = useMemo(() => {
    if (isMobile) {
      return {
        fullWidth: mainScreenUsableSize.width,
        fullHeight: mainScreenUsableSize.height,
        withChatWidth: mainScreenUsableSize.width / 2,
        withChatHeight: mainScreenUsableSize.height,
        localUserWidth: 120,
        localUserHeight: 160,
        muteIconSize: 24,
        muteIconSizeContainer: 30,
        muteIconContainerBottom: 24 + 8,
        muteIconContainerLeft: 8,
        controlContainerHeight: videoCallControlContainerHeightMobile,
        controlContainerHeightExpanded:
          videoCallControlContainerHeightMobileExpanded,
      };
    }
    return {
      fullWidth: mainScreenUsableSize.width,
      fullHeight: mainScreenUsableSize.height,
      withChatWidth: mainScreenUsableSize.width / 2,
      withChatHeight: mainScreenUsableSize.height,
      localUserWidth: 300,
      localUserHeight: 200,
      muteIconSize: 48,
      muteIconSizeContainer: 60,
      muteIconContainerBottom: 36 + 8,
      muteIconContainerLeft: 8,
      controlContainerHeight: videoCallControlContainerHeight,
      controlContainerHeightExpanded: videoCallControlContainerHeight,
    };
  }, [mainScreenUsableSize]);

  const videoCallScreenSizeMedical = useMemo(() => {
    if (isMobile) {
      return {
        fullWidth: mainScreenUsableSizeMedical.width,
        fullHeight: mainScreenUsableSizeMedical.height,
        withChatWidth: mainScreenUsableSizeMedical.width / 2,
        withChatHeight: mainScreenUsableSizeMedical.height,
        localUserWidth: 120,
        localUserHeight: 160,
        muteIconSize: 24,
        muteIconSizeContainer: 30,
        muteIconContainerBottom: 24 + 8,
        muteIconContainerLeft: 8,
        controlContainerHeight: videoCallControlContainerHeightMobile,
        controlContainerHeightExpanded:
          videoCallControlContainerHeightMobileExpanded,
      };
    }
    return {
      fullWidth: mainScreenUsableSizeMedical.width,
      fullHeight: mainScreenUsableSizeMedical.height,
      withChatWidth: mainScreenUsableSizeMedical.width / 2,
      withChatHeight: mainScreenUsableSizeMedical.height,
      localUserWidth: 300,
      localUserHeight: 200,
      muteIconSize: 48,
      muteIconSizeContainer: 60,
      muteIconContainerBottom: 36 + 8,
      muteIconContainerLeft: 8,
      controlContainerHeight: videoCallControlContainerHeight,
      controlContainerHeightExpanded: videoCallControlContainerHeight,
    };
  }, [mainScreenUsableSizeMedical, isMobile]);

  const chatMessageScreenHeight = useMemo(() => {
    return mainScreenUsableSizeMedical.height;
  }, [windowDimensions, headerHeight, footerHeight]);

  const chatMessageScreenHeightMedical = useMemo(() => {
    return mainScreenUsableSizeMedical.height;
  }, [windowDimensions, medicalHeaderHeight, mainScreenPaddingVertical]);

  return {
    windowDimensions,
    isMobile,
    isTablet,
    headerHeight,
    footerHeight,
    useWindowRatio,
    chatMessageScreenHeight,
    mainScreenSize,
    mainScreenUsableSize,
    mainScreenUsableSizeDialog,
    videoCallScreenSize,
    isDesktop,
    LogoSize,
    LogoTextSize,
    mainScreenPadding,
    infoDialogSize,

    medicalHeaderHeight,
    medicalSideBarWidth,
    mainScreenSizeMedical,
    mainScreenUsableSizeMedical,
    videoCallScreenSizeMedical,
    chatMessageScreenHeightMedical,
  };
}

// Detect iOS device
export const isIOS = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
};

// Check if device has home indicator (iPhone X and later, iPad Pro)
export const hasHomeIndicator = (): boolean => {
  if (typeof window === 'undefined' || !isIOS()) return false;
  // Devices with home indicator typically have these screen dimensions
  const height = window.screen.height;
  const width = window.screen.width;
  const minDimension = Math.min(height, width);
  const maxDimension = Math.max(height, width);

  // iPhone X and later have height >= 812 or width >= 812
  // iPad Pro 11" and later have similar dimensions
  return minDimension >= 812 || maxDimension >= 812;
};
// Calculate padding bottom based on iOS detection and safe area
export const SafeAreaPaddingBottom = () => {
  // Check if mobile (width < 800)
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 800;

  // For non-mobile views, return 0
  if (!isMobile) {
    return '0px';
  }

  const basePadding = 24; // Base padding for all devices

  if (isIOS() && hasHomeIndicator()) {
    // For iOS devices with home indicator, use CSS env() for safe area
    // env(safe-area-inset-bottom) will be 0 on devices without home indicator
    // and ~34px on devices with home indicator (iPhone X and later)
    return `calc(${basePadding}px + env(safe-area-inset-bottom, 34px))`;
  }

  return `${basePadding}px`;
};
